import React, { useEffect, useState } from 'react';
import { Image, type ImageProps } from 'react-bootstrap';

interface SafeImageProps extends ImageProps {
  fallbackSrc?: string;
  fallbackName?: string | null;
}

const PALETTE = [
  'bg-light-primary text-primary',
  'bg-light-success text-success',
  'bg-light-info text-info',
  'bg-light-warning text-warning',
  'bg-light-danger text-danger',
];

const paletteFor = (name: string) => {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return PALETTE[Math.abs(hash) % PALETTE.length];
};

const initialOf = (name: string) => name.trim().charAt(0).toLocaleUpperCase('pt-BR');

const SafeImage: React.FC<SafeImageProps> = ({
  src,
  fallbackSrc = '/media/avatars/NoImage.svg',
  fallbackName,
  onError,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(src || fallbackSrc);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setImageSrc(src || fallbackSrc);
    setFailed(false);
  }, [src, fallbackSrc]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setFailed(true);

    // Primeira prioridade: fallbackSrc definido pelo usuário
    // Segunda prioridade: imagem padrão predefinida
    if (imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
    }

    // Chama qualquer manipulador de erro adicional passado pelo usuário
    if (onError) {
      onError(e);
    }
  };

  // Com nome, a ausência ou falha da imagem vira um avatar com a inicial; sem nome (a API manda null), a imagem padrão
  if (fallbackName?.trim() && (!src || failed)) {
    const size = Number(props.width) || 36;
    const shape = props.roundedCircle ? 'rounded-circle' : 'rounded';

    return (
      <span
        aria-hidden="true"
        className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-bold ${shape} ${paletteFor(fallbackName)} ${props.className ?? ''}`}
        style={{ width: size, height: size, fontSize: size * 0.4 }}
      >
        {initialOf(fallbackName)}
      </span>
    );
  }

  return (
    <Image // Changed from SafeImage to img
      {...props}
      src={imageSrc}
      onError={handleImageError}
    />
  );
};

export default SafeImage;
